import { AnimatePresence, motion, useMotionValue, useReducedMotion, useSpring } from "motion/react";
import { useEffect, useRef, useState } from "react";

/**
 * A fluid cursor that trails, snaps and speaks.
 *
 * Three behaviours, in order of how much they matter:
 *
 *   **Trailing.** Position runs through a spring, so the shape arrives a beat
 *   after the pointer and settles rather than stopping. Tracking exactly
 *   makes it a second cursor; the lag is the entire effect.
 *
 *   **Magnetic snapping.** Over a link or a button it leaves the pointer
 *   entirely, moves to the element's centre and grows to wrap its box —
 *   borrowing the element's own border radius so it wraps a pill as a pill
 *   and a card as a card. It sticks there while the pointer moves inside.
 *
 *   **Context.** Anything carrying `data-cursor` puts that word inside the
 *   shape ("VIEW", "DRAG"), and the shape scales up to hold it.
 *
 * **The native cursor is never hidden.** That is the fashionable half of this
 * effect and it is a real regression: the OS pointer can be resized,
 * recoloured and given a trail, and those are settings people rely on to find
 * it at all. Replacing it silently overrides every one of them. This is a
 * layer on top, not a substitute — which also means that if any of the maths
 * below is wrong, the worst case is a stray decoration, not a lost pointer.
 *
 * Off entirely on coarse pointers and under reduced motion: there is no
 * cursor to decorate on a touchscreen, and an object chasing the pointer is
 * exactly the unrequested motion that preference asks to suppress.
 */

const INTERACTIVE =
  'a[href], button:not([disabled]), input:not([disabled]), textarea:not([disabled]), select, summary, [role="button"]';

/** Breathing room around a snapped element, in pixels. */
const SNAP_PADDING = 10;
/** The resting dot. */
const IDLE_SIZE = 26;

interface Target {
  x: number;
  y: number;
  width: number;
  height: number;
  radius: number;
  label: string | null;
}

export function Cursor() {
  const reduced = useReducedMotion() ?? false;
  // Lazy initial state rather than a setState inside an effect: the media
  // query is answerable on the first render, so deferring it would render
  // once as "off" and immediately cascade a re-render.
  //
  // `hover: hover` as well as `pointer: fine` — a stylus reports a fine
  // pointer but has no hover state to anticipate.
  const [finePointer] = useState(
    () => window.matchMedia("(pointer: fine) and (hover: hover)").matches,
  );
  const enabled = finePointer && !reduced;

  const [visible, setVisible] = useState(false);
  const [label, setLabel] = useState<string | null>(null);
  const [snapped, setSnapped] = useState(false);

  const x = useMotionValue(-200);
  const y = useMotionValue(-200);
  const width = useMotionValue(IDLE_SIZE);
  const height = useMotionValue(IDLE_SIZE);
  const radius = useMotionValue(IDLE_SIZE / 2);

  // Position is looser than size: the trail should be visible, but a box that
  // wobbles around its element for half a second looks broken rather than
  // fluid, so width/height/radius are stiffer and better damped.
  const springX = useSpring(x, { stiffness: 260, damping: 26, mass: 0.55 });
  const springY = useSpring(y, { stiffness: 260, damping: 26, mass: 0.55 });
  const springW = useSpring(width, { stiffness: 340, damping: 32, mass: 0.5 });
  const springH = useSpring(height, { stiffness: 340, damping: 32, mass: 0.5 });
  const springR = useSpring(radius, { stiffness: 340, damping: 32, mass: 0.5 });

  /** The element currently snapped to, so its rect is measured on change
   *  rather than on every single pointermove. */
  const held = useRef<Element | null>(null);

  useEffect(() => {
    if (!enabled) return;

    function measure(element: Element): Target {
      const box = element.getBoundingClientRect();
      const styles = getComputedStyle(element);
      // Borrow the element's own radius so the shape wraps a pill as a pill
      // and a card as a card. Fall back to fully round for anything square.
      const own = Number.parseFloat(styles.borderTopLeftRadius) || 0;
      const context = element.closest<HTMLElement>("[data-cursor]");
      return {
        x: box.left + box.width / 2,
        y: box.top + box.height / 2,
        width: box.width + SNAP_PADDING * 2,
        height: box.height + SNAP_PADDING * 2,
        radius: own > 0 ? own + SNAP_PADDING : Math.min(box.height, box.width) / 2 + SNAP_PADDING,
        label: context?.dataset.cursor ?? null,
      };
    }

    function apply(target: Target | null, pointerX: number, pointerY: number) {
      if (target) {
        x.set(target.x);
        y.set(target.y);
        width.set(target.width);
        height.set(target.height);
        radius.set(target.radius);
        setLabel(target.label);
        setSnapped(true);
        return;
      }
      x.set(pointerX);
      y.set(pointerY);
      width.set(IDLE_SIZE);
      height.set(IDLE_SIZE);
      radius.set(IDLE_SIZE / 2);
      setLabel(null);
      setSnapped(false);
    }

    function onMove(event: PointerEvent) {
      setVisible(true);
      const node = event.target instanceof Element ? event.target : null;
      const hit = node?.closest(INTERACTIVE) ?? null;

      // A context word without an interactive ancestor still deserves the
      // treatment — a project image is a thing you hover, not a thing you
      // click, and it should still be able to say VIEW.
      const contextOnly = hit ? null : (node?.closest("[data-cursor]") ?? null);
      const anchor = hit ?? contextOnly;

      if (anchor) {
        // Re-measure only when the element changes. Rects are layout reads,
        // and doing one per pointermove is the classic way this effect turns
        // into a scroll-jank complaint.
        if (held.current !== anchor) {
          held.current = anchor;
          apply(measure(anchor), event.clientX, event.clientY);
        }
        return;
      }

      held.current = null;
      apply(null, event.clientX, event.clientY);
    }

    function release() {
      held.current = null;
      setVisible(false);
      setSnapped(false);
      setLabel(null);
    }

    /** A snapped element that scrolls away must be re-measured or released. */
    function onScroll() {
      const anchor = held.current;
      if (!anchor) return;
      if (!anchor.isConnected) {
        held.current = null;
        setSnapped(false);
        setLabel(null);
        return;
      }
      const target = measure(anchor);
      x.set(target.x);
      y.set(target.y);
    }

    window.addEventListener("pointermove", onMove, { passive: true });
    document.addEventListener("pointerleave", release);
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      window.removeEventListener("pointermove", onMove);
      document.removeEventListener("pointerleave", release);
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
    };
  }, [enabled, x, y, width, height, radius]);

  if (!enabled) return null;

  return (
    <motion.div
      aria-hidden="true"
      style={{
        x: springX,
        y: springY,
        width: springW,
        height: springH,
        borderRadius: springR,
      }}
      animate={{ opacity: visible ? 1 : 0 }}
      transition={{ duration: 0.25 }}
      className={`pointer-events-none fixed top-0 left-0 z-[60] hidden -translate-x-1/2 -translate-y-1/2 items-center justify-center border transition-colors duration-300 lg:flex ${
        snapped ? "border-accent-dim bg-accent/[0.07]" : "border-accent-dim bg-accent/[0.14]"
      }`}
    >
      <span className="absolute inset-0 rounded-[inherit] bg-glow/15 blur-md" />
      <AnimatePresence>
        {label && (
          <motion.span
            key={label}
            initial={{ opacity: 0, scale: 0.7 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.8 }}
            transition={{ type: "spring", stiffness: 320, damping: 24 }}
            className="relative rounded-full bg-accent px-3 py-1.5 font-mono text-[0.625rem] tracking-[0.18em] text-canvas uppercase"
          >
            {label}
          </motion.span>
        )}
      </AnimatePresence>
    </motion.div>
  );
}
